import React from 'react';
import * as LucideIcons from 'lucide-react';
import { HelpCircle } from 'lucide-react';

// Custom LinkedIn Icon Component
const LinkedInIcon = ({ size = 24, color = "currentColor", className = "", ...props }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        fill={color} 
        className={className}
        viewBox="0 0 16 16"
        {...props}
    >
        <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854zm4.943 12.248V6.169H2.542v7.225zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248S2.4 3.226 2.4 3.934c0 .694.521 1.248 1.327 1.248zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016l.016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225z"/>
    </svg>
);

// Custom Bluesky (Butterfly) Icon Component
const BlueskyIcon = ({ size = 24, color = "currentColor", className = "", ...props }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        fill={color}
        className={className}
        viewBox="0 0 568 501"
        {...props}
    >
        <path d="M123.121 33.664C188.241 82.553 258.281 181.68 284 234.873c25.719-53.192 95.759-152.32 160.879-201.21C491.866-1.611 568-28.906 568 57.947c0 17.346-9.945 145.713-15.778 166.555-20.275 72.453-94.155 90.933-159.875 79.748C507.222 323.8 536.444 388.56 473.333 453.32c-119.86 122.992-172.272-30.859-185.702-70.281-2.462-7.227-3.614-10.608-3.631-7.733-.017-2.875-1.169.506-3.631 7.733-13.43 39.422-65.842 193.273-185.702 70.281-63.111-64.76-33.889-129.52 80.986-149.071-65.72 11.185-139.6-7.295-159.875-79.748C10.945 203.659 1 75.291 1 57.946 1-28.906 76.135-1.612 123.121 33.664Z" />
    </svg>
);

// Custom Mastodon Icon Component
const MastodonIcon = ({ size = 24, color = "currentColor", className = "", ...props }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        fill={color}
        className={className}
        viewBox="0 0 16 16"
        {...props}
    >
        <path d="M11.19 12.195c2.016-.24 3.77-1.475 3.99-2.603.348-1.778.32-4.339.32-4.339 0-3.47-2.286-4.488-2.286-4.488C12.062.238 10.083.017 8.027 0h-.05C5.92.017 3.942.238 2.79.765 2.79.765.504 1.783.504 5.253c0 .794-.015 1.743.009 2.748.071 2.98.532 5.92 3.204 6.647 1.233.335 2.292.405 3.145.357 1.547-.087 2.415-.56 2.415-.56l-.05-1.12s-1.107.348-2.35.306c-1.227-.042-2.523-.134-2.722-1.655a3.2 3.2 0 0 1-.028-.46s1.207.295 2.738.365c.936.043 1.813-.055 2.708-.166zm1.912-2.37V5.588c0-.898-.226-1.612-.68-2.141-.47-.529-1.084-.798-1.848-.798-.883 0-1.553.34-1.996 1.02L8 4.527l-.478-.858c-.443-.68-1.113-1.02-1.996-1.02-.764 0-1.379.269-1.849.798-.453.529-.679 1.243-.679 2.141v4.237h1.677V5.726c0-.898.38-1.354 1.14-1.354.839 0 1.26.541 1.26 1.613v2.252h1.668V5.985c0-1.072.42-1.613 1.26-1.613.76 0 1.14.456 1.14 1.354v4.1z" />
    </svg>
);

// Custom X (Twitter) Icon Component
const XIcon = ({ size = 24, color = "currentColor", className = "", ...props }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        fill={color} 
        className={className}
        viewBox="0 0 16 16"
        {...props}
    >
        <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/>
    </svg>
);

function Icon({
    name,
    size = 24,
    color = "currentColor",
    className = "",
    strokeWidth = 2,
    ...props
}) {
    // Handle custom icons first
    if (name === 'Linkedin') {
        return <LinkedInIcon size={size} color={color} className={className} {...props} />;
    }
    
    if (name === 'X') {
        return <XIcon size={size} color={color} className={className} {...props} />;
    }

    if (name === 'Bluesky') {
        return <BlueskyIcon size={size} color={color} className={className} {...props} />;
    }

    if (name === 'Mastodon') {
        return <MastodonIcon size={size} color={color} className={className} {...props} />;
    }
    
    const IconComponent = LucideIcons?.[name];

    if (!IconComponent) {
        return <HelpCircle size={size} color="gray" strokeWidth={strokeWidth} className={className} {...props} />;
    }

    return <IconComponent
        size={size}
        color={color}
        strokeWidth={strokeWidth}
        className={className}
        {...props}
    />;
}
export default Icon;